import { DialogContentText } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import FilterBar from "@saleor/components/FilterBar";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SearchBar from "@saleor/components/SearchBar";
import { useBulkVoucherListDownload } from "@saleor/discounts/queries";
import { BulkVoucherTabList_voucherGroups_edges_node } from "@saleor/discounts/types/BulkVoucherTabList";
import { VoucherListUrlSortField } from "@saleor/discounts/urls";
import { voucherDownload } from "@saleor/discounts/utils/utils";
import { getFilterVariables } from "@saleor/discounts/views/VoucherList/filters";
import useDownloadCsv from "@saleor/hooks/useDownloadCsv";
import useNotifier from "@saleor/hooks/useNotifier";
import { sectionNames } from "@saleor/intl";
import {
  FilterPageProps,
  ListActions,
  PageListProps,
  SortPage,
  TabPageProps
} from "@saleor/types";
import Moment from "moment";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { VoucherList_vouchers_edges_node } from "../../types/VoucherList";
import VoucherGroupList from "../VoucherGroupList";
import VoucherList from "../VoucherList";
import {
  createFilterStructure,
  VoucherFilterKeys,
  VoucherListFilterOpts
} from "./filters";

export interface VoucherListPageProps
  extends PageListProps,
    ListActions,
    FilterPageProps<VoucherFilterKeys, VoucherListFilterOpts>,
    SortPage<VoucherListUrlSortField>,
    TabPageProps {
  defaultCurrency: string;
  vouchers: VoucherList_vouchers_edges_node[];
  voucherGroups: BulkVoucherTabList_voucherGroups_edges_node[];
  customAction;
  activeTab;
  params;
  paginationState;
  onArrowClick: (id?: string) => () => void;
}

const VoucherListPage: React.FC<VoucherListPageProps> = ({
  activeTab,
  currencySymbol,
  currentTab,
  filterOpts,
  initialSearch,
  onAdd,
  onAll,
  pageInfo,
  onFilterChange,
  onSearchChange,
  paginationState,
  params,
  onTabChange,
  onTabDelete,
  onTabSave,
  customAction,
  tabs,
  ...listProps
}) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [downloadPagination, setDownloadPagination] = useState("");
  const [loadData, setLoadData] = useState(true);
  const [downloadData, setDownloadData] = useState([]);
  const [bulkGroupID, setBulkGroupID] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");

  const dateNow = Moment(new Date()).format("_YYYYMMDD_HHmmss");
  const fileName = `${voucherDownload.bulkVoucherDownload.filename}${dateNow}`;

  const reportVariables = React.useMemo(
    () => ({
      ...paginationState,
      after: downloadPagination,
      filter: getFilterVariables(params),
      first: 100,
      id: bulkGroupID
    }),
    [params, bulkGroupID]
  );

  // const [openModal, closeModal] = createDialogActionHandlers<
  //   BulkVoucherListUrlDialog,
  //   BulkVoucherListUrlQueryParams
  // >(navigate, params => bulkVoucherListUrl(id, params), params);

  const {
    data: bulkVoucherData,
    loading: bulkVoucherDataLoading,
    refetch: refetchDownload
  } = useBulkVoucherListDownload({
    displayLoader: true,
    skip: loadData,
    variables: reportVariables
  });

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([]);
    setDownloadStatus("default");
    setDownloadPagination("");
    setBulkGroupID(null);
  };

  const selectReportData = reportData => {
    const selectedData = reportData?.map(data => {
      delete data.__typename;
      delete data.id;
      delete data.used;
      data.groupCode = data?.group?.code;
      data.minSpent =
        data?.minSpent?.amount > 0 ? data?.minSpent?.amount : "None";
      data.endDate = data?.endDate
        ? Moment.tz(new Date(data?.endDate), "Asia/Manila").format("YYYY-MM-DD")
        : null;
      data.startDate = data?.startDate
        ? Moment.tz(new Date(data?.startDate), "Asia/Manila").format(
            "YYYY-MM-DD"
          )
        : null;
      data.isHidden = data?.isHidden ? "Yes" : "No";
      data.applyOncePerCustomer = data?.applyOncePerCustomer ? "Yes" : "No";
      data.isLimitToNewCustomer = data?.isLimitToNewCustomer ? "Yes" : "No";
      data.usageLimit = data?.usageLimit ? data?.usageLimit : "None";

      return data;
    });
    return selectedData;
  };

  const allVoucherGroup = bulkVoucherData?.voucherGroup?.vouchers?.edges?.map(
    edge => edge.node
  );
  const nextPage = bulkVoucherData?.voucherGroup?.vouchers?.pageInfo;

  useEffect(() => {
    if (bulkVoucherData && bulkGroupID && !bulkVoucherDataLoading) {
      setDownloadData(current => [...current, allVoucherGroup]);
      setDownloadPagination(nextPage?.endCursor);
      if (!nextPage?.hasNextPage && nextPage?.endCursor === null) {
        const selectedReportData = selectReportData(downloadData?.flat());
        useDownloadCsv(
          selectedReportData,
          voucherDownload.bulkVoucherDownload.fields,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([]);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Voucher group successfully downloaded."
          })
        });
        setBulkGroupID(null);
      }
    } else if (bulkGroupID) {
      refetchDownload();
    }
  }, [bulkVoucherData]);

  const structure = createFilterStructure(intl, filterOpts);

  const breadcrumbDirectory = [
    {
      name: "Vouchers"
    }
  ];

  return (
    <Container>
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <PageHeader title={intl.formatMessage(sectionNames.vouchers)}>
        <Button
          onClick={onAdd}
          variant="contained"
          color="primary"
          id="createVoucherBtn"
        >
          <FormattedMessage
            defaultMessage="Create voucher"
            description="button"
          />
        </Button>
      </PageHeader>
      <Card>
        {!activeTab || activeTab === "0" ? (
          <>
            <FilterBar
              // allTabLabel={intl.formatMessage({
              //   defaultMessage: "All",
              //   description: "tab name"
              // })}
              currencySymbol={currencySymbol}
              currentTab={currentTab}
              filterStructure={structure}
              initialSearch={initialSearch}
              searchPlaceholder={intl.formatMessage({
                defaultMessage: "Search Voucher"
              })}
              tabs={tabs}
              // onAll={onAll}
              onFilterChange={onFilterChange}
              onSearchChange={onSearchChange}
              onTabChange={onTabChange}
              onTabDelete={null}
              onTabSave={null}
              disablePlusOneTab={true}
              hideAction={true}
              customAction={customAction}
            />

            <VoucherList
              activeTab={activeTab}
              {...listProps}
              pageInfo={pageInfo}
            />
          </>
        ) : (
          <>
            <SearchBar
              // allTabLabel={intl.formatMessage({
              //   defaultMessage: "All",
              //   description: "tab name"
              // })}
              // currencySymbol={currencySymbol}
              currentTab={currentTab}
              // filterStructure={structure}
              initialSearch={initialSearch}
              searchPlaceholder={intl.formatMessage({
                defaultMessage: "Search Bulk Voucher"
              })}
              tabs={tabs}
              // onAll={onAll}
              // onFilterChange={onFilterChange}
              onSearchChange={onSearchChange}
              onTabChange={onTabChange}
              onTabDelete={null}
              onTabSave={null}
              disablePlusOneTab={true}
              hideAction={true}
              customAction={customAction}
            />
            <VoucherGroupList
              activeTab={activeTab}
              onBulkGroupListDownload={id => setBulkGroupID(id)}
              pageInfo={pageInfo}
              {...listProps}
            />

            <ActionDialog
              open={bulkGroupID != null}
              onClose={cancelDownload}
              confirmButtonState={downloadStatus}
              onConfirm={() => {
                setLoadData(false);
                setDownloadStatus("loading");
                setDownloadData([]);
                setDownloadPagination("");
                refetchDownload();
              }}
              variant="default"
              title={intl.formatMessage({
                defaultMessage: "Download Voucher Group List",
                description: "dialog title"
              })}
            >
              <DialogContentText>
                <FormattedMessage defaultMessage="You are about to download a CSV file, Do you want to continue?" />
              </DialogContentText>
            </ActionDialog>
          </>
        )}
      </Card>
    </Container>
  );
};
VoucherListPage.displayName = "VoucherListPage";
export default VoucherListPage;
